import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  User, Shield, Lock, RefreshCw, LogOut, KeyRound, Eye, EyeOff,
  Sparkles, Smartphone, ShieldCheck, AlertTriangle, Save, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import PwaInstallCard from '@/components/PwaInstallCard';
import PushNotificationsCard from '@/components/PushNotificationsCard';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

export default function Profile() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  // Phase 264d — Auto-extend session NOW defaults to ON. The toggle
  // remains visible so security-conscious owners / SAs can still opt
  // out, but new users (and existing users who never touched it) get
  // the better behaviour by default. Initial state mirrors the new
  // backend default so the UI never flickers off→on while loading.
  const [prefs, setPrefs] = useState({ auto_extend_session: true });
  const [savingPrefs, setSavingPrefs] = useState(false);

  // Change password
  const [pwForm, setPwForm] = useState({ current: '', new: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });
  const [changingPw, setChangingPw] = useState(false);

  // Sign out everywhere
  const [showLogoutAll, setShowLogoutAll] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  // Phase 261i — Feature flag for the install card so super admins can
  // run install-surface A/B tests. Default true preserves current behaviour.
  const showInstallCard = useFeatureFlag('show_pwa_install_card', true);

  // Phase 261j — Feature flag for the push notifications card so super admins
  // can stage the rollout. Default true keeps it visible.
  const showPushCard = useFeatureFlag('show_pwa_push_card', true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/users/me/preferences');
        setPrefs({
          // Phase 264d — Coerce missing value to the new default (ON).
          // Old user docs that never had preferences.auto_extend_session
          // set must STILL come back as ON in the UI.
          auto_extend_session: r.data?.auto_extend_session !== false,
          ...r.data,
        });
      } catch (e) { /* defaults */ }
      finally { setLoading(false); }
    })();
  }, []);

  const togglePref = async (key, value) => {
    setSavingPrefs(true);
    setPrefs(prev => ({ ...prev, [key]: value }));
    try {
      await api.patch('/users/me/preferences', { [key]: value });
      toast.success(value ? 'Auto-extend session enabled' : 'Auto-extend session disabled');
      // Notify the heartbeat hook to re-evaluate
      try { window.dispatchEvent(new CustomEvent('outplay:prefs-changed')); } catch (e) {}
    } catch (err) {
      // Revert on error
      setPrefs(prev => ({ ...prev, [key]: !value }));
      toast.error('Failed to save preference');
    } finally {
      setSavingPrefs(false);
    }
  };

  const passwordStrength = (pw) => {
    if (!pw) return { score: 0, label: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const labels = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong'];
    const colors = ['bg-red-500', 'bg-red-400', 'bg-amber-400', 'bg-emerald-400', 'bg-emerald-500', 'bg-emerald-600'];
    return { score, label: labels[score] || '', color: colors[score] || 'bg-slate-300' };
  };
  const pwStrength = passwordStrength(pwForm.new);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new !== pwForm.confirm) { toast.error('New passwords do not match'); return; }
    if (pwForm.new.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setChangingPw(true);
    try {
      const res = await api.post('/auth/change-password', { current_password: pwForm.current, new_password: pwForm.new });
      if (res.data.access_token) {
        // Backend issued a fresh token so this session stays alive
        localStorage.setItem('outplay_token', res.data.access_token);
      }
      toast.success('Password changed. All other sessions have been signed out.');
      setPwForm({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPw(false);
    }
  };

  const handleLogoutAll = async () => {
    setLoggingOutAll(true);
    try {
      const res = await api.post('/auth/logout-all');
      if (res.data.access_token) {
        localStorage.setItem('outplay_token', res.data.access_token);
      }
      toast.success(res.data.message || 'Signed out of all other devices');
      setShowLogoutAll(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoggingOutAll(false);
    }
  };

  if (loading) return (
    <div className="space-y-4 max-w-3xl">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-32 rounded-xl" />
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="profile-page">
      {/* Hero */}
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
          {(user?.name || user?.email || 'U')[0].toUpperCase()}
        </div>
        <div>
          <h1 className="text-2xl font-display font-semibold" data-testid="profile-name">{user?.name || 'My Profile'}</h1>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
          <Badge variant="outline" className="mt-1 capitalize">{(user?.role || '').replace('_', ' ')}</Badge>
        </div>
      </div>

      <Tabs defaultValue="security">
        <TabsList>
          <TabsTrigger value="security" data-testid="tab-security"><Shield className="h-4 w-4 mr-1" /> Security</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="h-4 w-4 mr-1" /> Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="security" className="space-y-4 mt-4">
          {/* Phase 264 — Device App + Push controls moved to the TOP of the
              Security tab so onboarding affordances are impossible to miss.
              Both cards use gesture-driven prompts (no auto-trigger). */}
          {showInstallCard && <PwaInstallCard />}
          {showPushCard && <PushNotificationsCard />}

          {/* Auto-extend session */}
          <Card data-testid="auto-extend-card" className="border-l-4 border-l-emerald-500">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-base flex items-center gap-2"><Activity className="h-5 w-5 text-emerald-600" /> Auto-extend session</CardTitle>
                  <CardDescription>Keep me signed in while I'm actively using Out-Play. The app silently refreshes my session every ~25 minutes.</CardDescription>
                </div>
                <Switch
                  checked={prefs.auto_extend_session}
                  onCheckedChange={(v) => togglePref('auto_extend_session', v)}
                  disabled={savingPrefs}
                  data-testid="auto-extend-toggle"
                />
              </div>
            </CardHeader>
            {prefs.auto_extend_session && (
              <CardContent>
                <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200">
                  <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-emerald-800 dark:text-emerald-200">
                    Your session will auto-renew while this tab is open. Closing the browser fully or signing out will still end your session.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Change Password */}
          <Card data-testid="change-pw-card" className="border-l-4 border-l-blue-500">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-5 w-5 text-blue-600" /> Change password</CardTitle>
              <CardDescription>For your security, all other devices will be signed out.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-3">
                {[
                  { key: 'current', label: 'Current password', placeholder: 'Enter current password' },
                  { key: 'new', label: 'New password', placeholder: 'Min 8 characters' },
                  { key: 'confirm', label: 'Confirm new password', placeholder: 'Repeat new password' },
                ].map(f => (
                  <div key={f.key} className="space-y-1.5">
                    <Label className="text-xs">{f.label}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type={showPw[f.key] ? 'text' : 'password'}
                        value={pwForm[f.key]}
                        onChange={(e) => setPwForm({ ...pwForm, [f.key]: e.target.value })}
                        placeholder={f.placeholder}
                        className="pl-9"
                        data-testid={`pw-${f.key}-input`}
                      />
                      <button type="button" onClick={() => setShowPw(s => ({ ...s, [f.key]: !s[f.key] }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPw[f.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {f.key === 'new' && pwForm.new && (
                      <div className="space-y-1">
                        <div className="flex gap-1">
                          {[0,1,2,3,4].map(i => (
                            <div key={i} className={`h-1.5 flex-1 rounded-full ${i < pwStrength.score ? pwStrength.color : 'bg-slate-200 dark:bg-slate-700'}`} />
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">Strength: <span className="font-medium">{pwStrength.label}</span></p>
                      </div>
                    )}
                  </div>
                ))}
                <Button type="submit" disabled={changingPw || !pwForm.current || !pwForm.new || !pwForm.confirm} className="w-full sm:w-auto" data-testid="change-pw-submit">
                  <Save className="h-4 w-4 mr-2" /> {changingPw ? 'Updating...' : 'Update password'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Sign out everywhere */}
          <Card data-testid="logout-all-card" className="border-l-4 border-l-amber-500">
            <CardHeader>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="text-base flex items-center gap-2"><Smartphone className="h-5 w-5 text-amber-600" /> Sign out of all other devices</CardTitle>
                  <CardDescription>Lost a phone? Suspect someone else has access? Instantly sign out from every other browser, tab and device.</CardDescription>
                </div>
                <Button variant="outline" onClick={() => setShowLogoutAll(true)} data-testid="logout-all-button" className="border-amber-300 hover:bg-amber-50">
                  <LogOut className="h-4 w-4 mr-1" /> Sign out everywhere
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Quick logout */}
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Sign out of this device</p>
                <p className="text-xs text-muted-foreground">Just this browser. Other devices will stay signed in.</p>
              </div>
              <Button variant="ghost" onClick={logout} data-testid="logout-button">
                <LogOut className="h-4 w-4 mr-1" /> Log out
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="profile" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Account information</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div><Label className="text-xs text-muted-foreground">Name</Label><p className="mt-0.5 font-medium">{user?.name || '—'}</p></div>
                <div><Label className="text-xs text-muted-foreground">Email</Label><p className="mt-0.5 font-medium">{user?.email}</p></div>
                <div><Label className="text-xs text-muted-foreground">Role</Label><p className="mt-0.5 font-medium capitalize">{(user?.role || '').replace('_', ' ')}</p></div>
                <div><Label className="text-xs text-muted-foreground">Phone</Label><p className="mt-0.5 font-medium">{user?.phone || '—'}</p></div>
              </div>
              <p className="text-xs text-muted-foreground italic mt-2">Profile editing UI coming soon — contact your academy admin to update these fields.</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showLogoutAll} onOpenChange={setShowLogoutAll}>
        <DialogContent data-testid="logout-all-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Sign out of all other devices?</DialogTitle>
            <DialogDescription>
              You'll stay signed in on this device, but every other browser, phone or tablet you've signed in with will be immediately signed out.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 dark:text-amber-100">
            <Sparkles className="h-3.5 w-3.5 inline mr-1" /> Tip: combine this with changing your password if you suspect your credentials were leaked.
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowLogoutAll(false)}>Cancel</Button>
            <Button onClick={handleLogoutAll} disabled={loggingOutAll} className="bg-amber-600 hover:bg-amber-700" data-testid="logout-all-confirm">
              {loggingOutAll ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <LogOut className="h-4 w-4 mr-2" />}
              Sign out everywhere
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
